import { createContext, useContext, useEffect, useState } from "react";
import {
  collection,
  getCountFromServer,
  doc,
  getDoc,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

const DashboardContext = createContext(null);

const CACHE_KEY = "admin_dashboard_cache";
const CACHE_DURATION = 1000 * 60 * 15; // 15 minutos

const defaultData = {
  metrics: {
    totalUsers: 0,
    totalAdmins: 0,
    totalMinigames: 0,
    activeMinigames: 0,
    totalStoreItems: 0,
    activeStoreItems: 0,
    totalMissions: 0,
    activeMissions: 0,
  },
  gameConfig: {
    currentSeason: "No configurada",
    currentSeasonEndAt: null,
    seasonEndDate: null,
    maintenanceMode: false,
    gameVersion: "N/A",
    dailyMissionIds: [],
    dailyMissionsUpdatedAt: null,
    dailyPeriodId: "No configurado",
  },
};

function serializeFirestoreDate(value) {
  if (!value) return null;

  if (value.toDate) {
    return value.toDate().toISOString();
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  return value;
}

function normalizeGameConfig(data = {}) {
  const seasonEnd =
    data.currentSeasonEndAt ||
    data.seasonEndDate ||
    data.currentSeasonEndDate ||
    null;

  return {
    currentSeason: data.currentSeason ?? "No configurada",

    currentSeasonEndAt: serializeFirestoreDate(data.currentSeasonEndAt),
    seasonEndDate: serializeFirestoreDate(seasonEnd),

    maintenanceMode: Boolean(data.maintenanceMode),
    gameVersion: data.gameVersion ?? "N/A",

    dailyMissionIds: Array.isArray(data.dailyMissionIds)
      ? data.dailyMissionIds
      : [],

    dailyMissionsUpdatedAt: serializeFirestoreDate(
      data.dailyMissionsUpdatedAt
    ),

    dailyPeriodId: data.dailyPeriodId ?? "No configurado",
  };
}

async function safeCount(collectionPath, filters = []) {
  try {
    const baseCollection = collection(db, collectionPath);

    const countQuery =
      filters.length > 0
        ? query(
            baseCollection,
            ...filters.map(([field, operator, value]) =>
              where(field, operator, value)
            )
          )
        : baseCollection;

    const snapshot = await getCountFromServer(countQuery);

    return snapshot.data().count;
  } catch (error) {
    console.warn(`No se pudo contar ${collectionPath}:`, error);
    return 0;
  }
}

export function DashboardProvider({ children }) {
  const [dashboardData, setDashboardData] = useState(defaultData);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [dashboardError, setDashboardError] = useState("");

  async function loadDashboardData({ forceRefresh = false } = {}) {
    setDashboardError("");

    try {
      const cached = sessionStorage.getItem(CACHE_KEY);

      if (!forceRefresh && cached) {
        const parsedCache = JSON.parse(cached);
        const isFresh = Date.now() - parsedCache.savedAt < CACHE_DURATION;

        if (isFresh) {
          setDashboardData(parsedCache.data);
          setLoadingDashboard(false);
          return;
        }
      }

      setLoadingDashboard(true);

      const [
        totalUsers,
        totalAdmins,
        totalMinigames,
        activeMinigames,
        totalStoreItems,
        activeStoreItems,
        totalMissions,
        activeMissions,
        configSnapshot,
      ] = await Promise.all([
        safeCount("users"),
        safeCount("users", [["role", "==", "admin"]]),
        safeCount("minigames"),
        safeCount("minigames", [["enabled", "==", true]]),

        // Si estas rutas no existen todav�a, devuelven 0 sin romper el dashboard.
        safeCount("config/shop/items"),
        safeCount("config/shop/items", [["enabled", "==", true]]),
        safeCount("config/game/missions"),
        safeCount("config/game/missions", [["active", "==", true]]),

        getDoc(doc(db, "config", "game")),
      ]);

      const gameConfig = configSnapshot.exists()
        ? normalizeGameConfig(configSnapshot.data())
        : defaultData.gameConfig;

      const freshData = {
        metrics: {
          totalUsers,
          totalAdmins,
          totalMinigames,
          activeMinigames,
          totalStoreItems,
          activeStoreItems,
          totalMissions,
          activeMissions,
        },
        gameConfig,
      };

      setDashboardData(freshData);

      sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          savedAt: Date.now(),
          data: freshData,
        })
      );
    } catch (error) {
      console.error("Error cargando dashboard:", error);
      setDashboardError("No se pudo cargar la informaci�n del dashboard.");
    } finally {
      setLoadingDashboard(false);
    }
  }

  useEffect(() => {
    loadDashboardData();
  }, []);

  return (
    <DashboardContext.Provider
      value={{
        dashboardData,
        loadingDashboard,
        dashboardError,
        refreshDashboard: () => loadDashboardData({ forceRefresh: true }),
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboard() {
  const context = useContext(DashboardContext);

  if (!context) {
    throw new Error("useDashboard debe usarse dentro de DashboardProvider");
  }

  return context;
}